import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchCapabilities,
  fetchVersion,
  isConnectedDeployment,
  type Capabilities,
  type ReleaseNote,
  type VersionInfo,
} from '../../api/meta';
import { registerHandle } from '../../app/handles';
import { callAction } from '../../app/actions';

/**
 * The About panel — the FIRST region ported to React.
 *
 * Chosen deliberately as the smallest real one: self-contained, its own data source, and it
 * already has both functional tests (`e2e/14-about.spec.ts`) and a pixel baseline. Those
 * tests must pass UNEDITED. That was the acceptance criterion for every step of the port,
 * and proving it on 150 lines is a great deal cheaper than discovering it on the map.
 *
 * IT REUSES THE EXISTING CSS. Same element ids, same class names, same structure — the
 * stylesheet in infostation.html is untouched. Rewriting the styling at the same time as the
 * markup would make a pixel diff impossible to interpret: you could not tell a porting
 * mistake from an intended change. The CSS moves later, on its own, with its own diff.
 *
 * The trigger stays vanilla for now. `#btn-about` lives inside the brand block, and porting
 * the topbar is its own step; it calls in through `window.InfoStationReact.about`.
 */

const FEATURES: [keyof Capabilities['ai'], string][] = [
  ['docgen', 'AI documentation'],
  ['translate', 'Document translation'],
  ['liveVerify', 'Live verification'],
  ['chat', 'Chatbot'],
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * '2026-08-24' -> '24 Aug 2026'.
 *
 * Deliberately not `toLocaleDateString`: the panel's own labels are English, like the rest of
 * the chrome, so a half-localised date would read as a bug rather than as a feature.
 */
function formatDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  if (!m) return iso ?? '';
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

function Deployment({ caps }: { caps: Capabilities | null }) {
  // Say nothing rather than guess. `caps` is null only when the server predates
  // /api/capabilities or the fetch failed, and a wrong answer here is worse than no answer.
  if (!caps) return null;

  const online = isConnectedDeployment(caps);
  return (
    <>
      <div
        className="ab-deploy"
        style={{ ['--dc' as string]: online ? 'var(--status-approved)' : 'var(--ink-faint)' }}
      >
        <span className="ab-dot" />
        <span>{online ? 'Connected deployment' : 'Offline deployment'}</span>
      </div>
      {FEATURES.map(([key, name]) => {
        const on = caps.ai[key] !== false;
        return (
          <div className={`ab-row${on ? '' : ' off'}`} key={key}>
            <span>{name}</span>
            <span className="ab-mark">{on ? '✓' : '—'}</span>
          </div>
        );
      })}
    </>
  );
}

function Release({ release, current }: { release: ReleaseNote; current: boolean }) {
  const head = (
    <>
      <span className="ab-relver">V{release.version}</span>
      <span className="ab-reldate">{formatDate(release.date)}</span>
    </>
  );
  const body = (
    <>
      {release.headline ? <div className="ab-line">{release.headline}</div> : null}
      {release.items?.length ? (
        <ul className="ab-items">
          {release.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      ) : null}
    </>
  );

  // The one you are running is the one you came to read, so it is always open. The rest fold
  // away: three expanded entries made the panel taller than the window, which is not a recap.
  if (current) {
    return (
      <div className="ab-rel">
        <div className="ab-relhead">
          {head}
          <span className="ab-now">Current</span>
        </div>
        {body}
      </div>
    );
  }
  return (
    <details className="ab-rel">
      <summary>{head}</summary>
      {body}
    </details>
  );
}

export interface AboutPanelHandle {
  /**
   * Subscribe to the open state. Returns an unsubscribe.
   *
   * The trigger (#btn-about) lives in the top bar, which is a different React root, so it
   * cannot read this component's state directly — and its `aria-expanded` has to stay honest
   * even when the panel closes on its own, on Escape or a click outside. This is that link.
   */
  subscribe: (listener: (open: boolean) => void) => () => void;
  toggle: () => void;
  close: () => void;
}

/**
 * `host` is the `#about` element the vanilla page provides, and the component renders INTO
 * it rather than wrapping itself in a div of its own.
 *
 * That is not a stylistic choice. The stylesheet targets `#about` and `#about.show` — the
 * padding, the border, the shadow and the whole open/closed rule live on the host. Rendering
 * a wrapper inside would leave the visible box owned by an element React does not control,
 * and toggling `show` on the wrapper would style nothing. So React owns the contents and
 * toggles the host's class; the CSS is untouched and the pixel baseline still applies.
 *
 * When the CSS moves into the client this inverts: the component grows its own root and the
 * host becomes an ordinary mount point.
 */
export function AboutPanel({
  host,
  onOpenChange,
}: {
  host: HTMLElement;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [version, setVersion] = useState<VersionInfo | null>(null);
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    host.classList.toggle('show', open);
    host.setAttribute('role', 'dialog');
    host.setAttribute('aria-label', 'About InfoStation');
    onOpenChange?.(open);
  }, [open, host, onOpenChange]);

  // Fetched on first open rather than at mount: the panel is a rarely-used affordance, and
  // the requests are cached in api/meta so a second open costs nothing.
  useEffect(() => {
    if (!open) return;
    let live = true;
    void fetchVersion().then(
      (v) => live && setVersion(v),
      () => live && setLoadFailed(true),
    );
    void fetchCapabilities().then(
      (c) => live && setCaps(c),
      () => {
        /* leave caps null: the deployment block is simply omitted */
      },
    );
    return () => {
      live = false;
    };
  }, [open]);

  // Escape, and a click anywhere outside. Capture phase, so this closes before whatever was
  // clicked acts — matching the dotted-link menu, which is the app's existing idiom.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (t && host.contains(t)) return;
      // The trigger toggles on its own; closing here too would immediately reopen it.
      if (t instanceof Element && t.closest('#btn-about')) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [open, host]);

  const listeners = useRef(new Set<(open: boolean) => void>());
  const api = useRef<AboutPanelHandle>({
    subscribe: () => () => {},
    toggle: () => {},
    close: () => {},
  });
  api.current.subscribe = useCallback((listener: (open: boolean) => void) => {
    listeners.current.add(listener);
    listener(openRef.current); // answer immediately: a late subscriber must not miss the state
    return () => {
      listeners.current.delete(listener);
    };
  }, []);
  api.current.toggle = useCallback(() => setOpen((o) => !o), []);
  api.current.close = useCallback(() => setOpen(false), []);

  // Kept in a ref as well as state so subscribe() can answer synchronously without making
  // itself depend on `open` — which would hand every subscriber a new function each toggle.
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
    for (const l of listeners.current) l(open);
  }, [open]);
  useEffect(() => {
    registerHandle('about', api.current);
  }, []);

  const shown = version?.version ?? '';

  // Nothing is rendered while closed: the host is display:none, and an empty panel keeps the
  // async title lookups and the release list out of the tree until they are needed.
  if (!open) return null;

  return (
    <>
      <div className="ab-head">
        <span className="ab-name">CONIGON InfoStation</span>
        <span className="ab-ver">V{shown}</span>
      </div>

      <Deployment caps={caps} />

      <div className="ab-sep" />
      {loadFailed || !version?.releases?.length ? (
        <div className="ab-note">
          Release notes could not be loaded from the server. The version above is the one this page was
          built with, which may be older than the server it is talking to.
        </div>
      ) : (
        <>
          <div className="ab-h">What&apos;s new</div>
          {version.releases.map((r, i) => (
            <Release key={r.version} release={r} current={i === 0} />
          ))}
        </>
      )}

      {/* The privacy page (features/privacy/PrivacyPanel.tsx). The panel closes first:
          it is a small recap, and the page it opens is full-screen. */}
      <div className="ab-sep" />
      <button
        type="button"
        className="ab-privacy"
        id="about-privacy"
        onClick={() => {
          setOpen(false);
          callAction('openPrivacy');
        }}
      >
        <svg className="ico">
          <use href="#i-shield" />
        </svg>
        Privacy &amp; data
      </button>
    </>
  );
}
